import { useQuery } from '@tanstack/react-query';
import React from 'react';

import {
  GltftScanModel,
  OrthoTileModel as OrthoTileModelV0,
  PointCloudModel,
  PointCloudModel as PointCloudModelV0,
} from '../../../../typings/api/skymap/rest/v0/.common';
import { GeodataModel, TiledModel } from '../../../../typings/api/skymap/rest/v1/.common';
import { BackendFeatureFlags } from '../../../../typings/backend-feature-flags';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../utils/variables';
import { Coordinates } from '../../viewer/utilities/math-helpers';
import { ProjectContext } from './project-state/project-state';

export type ArtifactData = {
  type: 'legacy-scan' | 'tiled-model' | 'pointcloud' | 'orthotile';
  id: string;
  name: string;
  size: number;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  origo: Coordinates;
  geodata?: GeodataModel;
};

export interface ProjectArtifactsContextValue {
  geodata: GeodataModel[];
  tiledModels: TiledModel[];
  gltfScans: GltftScanModel[];
  orthoTiles: OrthoTileModelV0[];
  pointClouds: PointCloudModelV0[];
  artifacts: ArtifactData[];
  isLoading: {
    geodata: boolean;
    tiledModels: boolean;
    gltfScans: boolean;
    orthoTiles: boolean;
    pointClouds: boolean;
    any: boolean;
  };
  refetch(this: void): Promise<void>;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const ProjectArtifactsContext = React.createContext<ProjectArtifactsContextValue>(
  undefined!,
);

type Props = {
  children: React.ReactNode;
};

function transformToArtifactData(
  geodata: GeodataModel[],
  orthoTiles: OrthoTileModelV0[],
  pointClouds: PointCloudModelV0[],
  tiledModels: TiledModel[],
  gltfScans: GltftScanModel[],
): ArtifactData[] {
  const artifacts: ArtifactData[] = [];

  // Transform orthotiles
  for (const item of orthoTiles) {
    artifacts.push({
      type: 'orthotile',
      id: item.id,
      name: item.fileName ?? item.id,
      size: Number(item.size),
      status:
        item.status === 'COMPLETED' ? 'COMPLETED' : item.status === 'FAILED' ? 'FAILED' : 'RUNNING',
      geodata: geodata.find((x) => x.id === item.parentGeodataId),
      origo: {
        easting: item.boundingBoxMin[0],
        northing: item.boundingBoxMin[1],
        height: item.boundingBoxMin[2],
      },
    });
  }

  // Transform point clouds
  for (const item of pointClouds) {
    artifacts.push({
      type: 'pointcloud',
      id: item.id,
      name: item.fileName ?? item.name,
      size: Number(item.size),
      status:
        item.status === 'COMPLETED' ? 'COMPLETED' : item.status === 'FAILED' ? 'FAILED' : 'RUNNING',
      geodata: geodata.find((x) => x.id === item.geodataId),
      origo: {
        easting: item.origo[0],
        northing: item.origo[1],
        height: item.origo[2],
      },
    });
  }

  // Transform tiled models
  for (const item of tiledModels) {
    artifacts.push({
      type: 'tiled-model',
      id: item.id,
      name: item.name,
      size: Number(item.size),
      status:
        item.status === 'COMPLETED' ? 'COMPLETED' : item.status === 'FAILED' ? 'FAILED' : 'RUNNING',
      geodata: geodata.find((x) => x.id === item.geodataId),
      origo: {
        easting: item.origo[0],
        northing: item.origo[1],
        height: item.origo[2],
      },
    });
  }

  // Transform legacy scans (gltf scans)
  for (const item of gltfScans) {
    artifacts.push({
      type: 'legacy-scan',
      id: item.id,
      name: item.name,
      size: Number(item.size),
      status: item.size === 0 ? 'FAILED' : 'COMPLETED',
      geodata: geodata.find((x) => x.id === item.parentGeodataId),
      origo: {
        easting: item.coordinates?.origo?.[0] ?? 0,
        northing: item.coordinates?.origo?.[1] ?? 0,
        height: item.coordinates?.origo?.[2] ?? 0,
      },
    });
  }

  return artifacts;
}

const ProjectArtifactsProvider = ({ children }: Props) => {
  const { project } = React.useContext(ProjectContext);
  const projectId = project?.id;

  // Fetch geodata
  const geodataQuery = useQuery({
    enabled: isDefined(projectId),
    queryKey: ['project-artifacts-geodata', projectId],
    queryFn: async () => {
      if (!projectId) {
        return [] as GeodataModel[];
      }

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
      const { data } = await projectService.getProjectGeodata({
        path: { projectId },
        query: {},
        headers: {
          'x-flag':
            'SK-6337-exclude-geodata-include-options-by-default' satisfies BackendFeatureFlags,
        },
      });

      return Array.isArray(data) ? data : [];
    },
    networkMode: 'always',
    staleTime: 60_000,
    retry: false,
  });

  // Fetch tiled models
  const tiledModelsQuery = useQuery({
    enabled: isDefined(projectId),
    queryKey: ['project-artifacts-tiled-models', projectId],
    queryFn: async () => {
      if (!projectId) {
        return [] as TiledModel[];
      }

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
      const { data } = await projectService.getTiledModels({
        path: { projectId },
      });

      return Array.isArray(data) ? data : [];
    },
    networkMode: 'always',
    staleTime: 60_000,
    retry: false,
  });

  // Fetch gltf scans (legacy scans)
  const gltfScansQuery = useQuery({
    enabled: isDefined(projectId),
    queryKey: ['project-artifacts-gltf-scans', projectId],
    queryFn: async () => {
      if (!projectId) {
        return [] as GltftScanModel[];
      }

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
      const response = await projectService.getScans({
        path: { projectId },
      });

      // v0 service returns { data: [...], message: string }
      return Array.isArray(response.data) ? response.data : [];
    },
    networkMode: 'always',
    staleTime: 60_000,
    retry: false,
  });

  // Fetch orthotiles
  const orthoTilesQuery = useQuery({
    enabled: isDefined(projectId),
    queryKey: ['project-artifacts-orthotiles', projectId],
    queryFn: async () => {
      if (!projectId) {
        return [] as OrthoTileModelV0[];
      }

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
      const response = await projectService.getOrthoTiles({
        path: { projectId },
      });

      return Array.isArray(response.data) ? response.data : [];
    },
    networkMode: 'always',
    staleTime: 60_000,
    retry: false,
  });

  // Fetch point clouds
  const pointCloudsQuery = useQuery({
    enabled: isDefined(projectId),
    queryKey: ['project-artifacts-point-clouds', projectId],
    queryFn: async () => {
      if (!projectId) {
        return [] as PointCloudModel[];
      }

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
      const response = await projectService.getPointClouds({
        path: { projectId },
      });

      // v0 service returns { data: [...], message: string }
      return Array.isArray(response.data) ? response.data : [];
    },
    networkMode: 'always',
    staleTime: 60_000,
    retry: false,
  });

  // Transform all artifacts into unified format
  const artifacts = React.useMemo(() => {
    return transformToArtifactData(
      geodataQuery.data ?? [],
      orthoTilesQuery.data ?? [],
      pointCloudsQuery.data ?? [],
      tiledModelsQuery.data ?? [],
      gltfScansQuery.data ?? [],
    );
  }, [
    geodataQuery.data,
    orthoTilesQuery.data,
    pointCloudsQuery.data,
    tiledModelsQuery.data,
    gltfScansQuery.data,
  ]);

  const isLoading = React.useMemo(
    () => ({
      geodata: geodataQuery.isLoading || geodataQuery.isRefetching,
      tiledModels: tiledModelsQuery.isLoading || tiledModelsQuery.isRefetching,
      gltfScans: gltfScansQuery.isLoading || gltfScansQuery.isRefetching,
      orthoTiles: orthoTilesQuery.isLoading || orthoTilesQuery.isRefetching,
      pointClouds: pointCloudsQuery.isLoading || pointCloudsQuery.isRefetching,
      any:
        geodataQuery.isLoading ||
        geodataQuery.isRefetching ||
        tiledModelsQuery.isLoading ||
        tiledModelsQuery.isRefetching ||
        gltfScansQuery.isLoading ||
        gltfScansQuery.isRefetching ||
        orthoTilesQuery.isLoading ||
        orthoTilesQuery.isRefetching ||
        pointCloudsQuery.isLoading ||
        pointCloudsQuery.isRefetching,
    }),
    [
      geodataQuery.isLoading,
      geodataQuery.isRefetching,
      tiledModelsQuery.isLoading,
      tiledModelsQuery.isRefetching,
      gltfScansQuery.isLoading,
      gltfScansQuery.isRefetching,
      orthoTilesQuery.isLoading,
      orthoTilesQuery.isRefetching,
      pointCloudsQuery.isLoading,
      pointCloudsQuery.isRefetching,
    ],
  );

  const refetch = React.useCallback(async () => {
    await Promise.all([
      geodataQuery.refetch(),
      tiledModelsQuery.refetch(),
      gltfScansQuery.refetch(),
      orthoTilesQuery.refetch(),
      pointCloudsQuery.refetch(),
    ]);
  }, [geodataQuery, tiledModelsQuery, gltfScansQuery, orthoTilesQuery, pointCloudsQuery]);

  const value: ProjectArtifactsContextValue = {
    geodata: geodataQuery.data ?? [],
    tiledModels: tiledModelsQuery.data ?? [],
    gltfScans: gltfScansQuery.data ?? [],
    orthoTiles: orthoTilesQuery.data ?? [],
    pointClouds: pointCloudsQuery.data ?? [],
    artifacts,
    isLoading,
    refetch,
  };

  return (
    <ProjectArtifactsContext.Provider value={value}>{children}</ProjectArtifactsContext.Provider>
  );
};

export { ProjectArtifactsProvider };
